(ns dashboard.core
  (:require [ajax.core :as ajax]
            [ajax.protocols]
            [day8.re-frame.http-fx] ;; registers :http-xhrio effect
            [cognitect.transit :as t]
            [dashboard.notifications :as notifications]
            [re-frame.core :as rf]
            [reagent.dom :as rdom]
            [reitit.core :as reitit]
            [reitit.frontend.easy :as rfe]
            [dashboard.effects :as effects]
            [dashboard.services.http-config :as http-config]
            [dashboard.db :as db]
            [dashboard.views :as views]
            [dashboard.routes :as routes]
            [dashboard.services.error-reporting :as error-reporting]
            [dashboard.browser-extension :as browser-extension])
  (:require-macros [io.repliance.util.version :refer [get-version-info]]))

(defn navigate! [match _]
  (rf/dispatch [::effects/navigate match]))

(defn start-router! []
  (rfe/start!
    (reitit/router routes/app-routes)
    navigate!
    {:use-fragment false}))

(defn wrapper []
  [:f> views/main-panel])

(defn mount-root []
  (rf/clear-subscription-cache!)
  (let [root-el (.getElementById js/document "app")]
    (rdom/unmount-component-at-node root-el)
    (rdom/render [wrapper] root-el)))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defn ^:dev/after-load after-reload []
  (mount-root))

(defn log-version-info []
  (.groupCollapsed js/console "Version Info:")
  (js/console.info (clj->js (get-version-info)))
  (.groupEnd js/console))

(def check-session-when-forbidden-by-api
  (ajax/to-interceptor
   {:name :check-session-when-forbidden-by-api
    :response (fn [response]
                (let [status (ajax.protocols/-status response)]
                  (when (= status 403)
                    (rf/dispatch [::check-session])))
                response)}))

;; I wonder if this could be done with re-frame instead in a non-viral manner
;; [Vesa M.} << LFA: What is actually being done here? Why are we doing this?
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defonce add-default-interceptors
  (reset! ajax/default-interceptors
          [check-session-when-forbidden-by-api]))

(defn- ->user-with-roles
  [user]
  (merge user
         (if (contains? (set (:privileges user)) "admin")
           {:roles #{"authenticated"}}
           {:roles #{"anonymous"}})))

(rf/reg-event-fx
  ::set-session
  (fn [{:keys [db]} [_ user]]
    (let [user-with-roles (->user-with-roles user)]
      {:db (assoc-in db [:user] user-with-roles)
       :fx [[:dispatch [::set-initialized]]
            [:dispatch [::start-session-check]]]})))

(rf/reg-event-fx
  ::set-anonymous
  (fn [{:keys [db]} [_ user]]
    (let [user-with-roles (merge user {:roles #{"anonymous"}})]
      {:db (assoc-in db [:user] user-with-roles)
       :fx [[:dispatch [::set-initialized]]
            [:dispatch [::start-session-check]]]})))

(rf/reg-event-fx
 ::expire-session
 (fn [{:keys [db]} _]
   (when-let [login-expiration-check (-> db :user :expiration-check)]
     (js/clearInterval login-expiration-check))

   {::effects/fx-redirect [:route.view/login
                           {:state (js/encodeURIComponent
                                    (.. js/window -location))}]
    :fx [[:dispatch [::set-anonymous]]
         [:dispatch [::notifications/add {:message "Login has been expired, please re-authenticate."}]]]}))

(rf/reg-event-fx
  ::check-session-against-user-info
  (fn [{:keys [db]} [_ user]]
    (let [current-roles (get-in db [:user :roles])
          new-roles (:roles (->user-with-roles user))]
      (cond
       ;; Session has expired on the background
       (and (contains? current-roles "authenticated")
            (not (contains? new-roles "authenticated")))
       (rf/dispatch [::expire-session])

       ;; Session has been renewed elsewhere (another tab/window/...)
       (and (not (contains? current-roles "authenticated"))
            (contains? new-roles "authenticated"))
       (.reload (.-location js/window))

       :else nil))))

(rf/reg-event-fx
 ::check-session
 (fn [& _]
   {:http-xhrio {:method :get
                 :uri "/api/user"
                 :timeout http-config/timeout
                 :response-format http-config/transit-response-format
                 :on-success [::check-session-against-user-info]
                 :on-failure [::notifications/add {:message "Session check
                                                             failed. Are you
                                                             connected to the
                                                             Internet?"}]}}))

(rf/reg-event-fx
  ::start-session-check
  (fn [{:keys [db]} _]
    (let [login-expiration-check (fn []
                                   (rf/dispatch [::check-session]))
          one-minute (* 60 1000)]
      {:db (assoc-in db [:user :expiration-check]
                     (.setInterval js/window
                                   login-expiration-check
                                   one-minute))})))

(rf/reg-event-fx
  ::initialize-user-session
  (fn [& _]
    {:http-xhrio {:method :get
                  :uri "/api/user"
                  :timeout http-config/timeout
                  :response-format http-config/transit-response-format
                  :on-success [::set-session]
                  :on-failure [::set-anonymous]}}))

(rf/reg-event-db
  ::set-initialized
  (fn [db _]
    (start-router!)
    (assoc-in db [:ui :initialized] true)))

(defn initialize-app [_ _]
  (let [initial-db db/default-db]
    {:db initial-db
     :fx [[:dispatch [::initialize-user-session]]]}))

(rf/reg-event-fx
  ::initialize-app
  initialize-app)

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defn init []
  (log-version-info)
  (error-reporting/enable-error-reporting!)
  (rf/dispatch-sync [::initialize-app])
  (js/window.addEventListener
   "message"
   (fn [event]
     (let [reader (t/reader :json {})
           event-data (try
                        (t/read reader (.. event -data))
                        (catch js/Error _e
                          (println "pass non-transit event")))]
       (when event-data
         (rf/dispatch [::browser-extension/receive-event event-data])))))
  (mount-root))
